import { CommonDialogs } from "@mcleod/common";
import { ImageName } from "@mcleod/images";
import { Api, ModelRow } from "@mcleod/core";
import { Button, DataSource, LookupModelSearchEvent, LookupModelSelectionEvent, Table, TableRow, Textbox } from "@mcleod/components";

/**
 * Utility class that provides common functionality for EDI Partner Qualifiers components.
 * Uses composition pattern to avoid inheritance issues.
 */
export class EdiPartnerQualifiersHelper {
    /**
     * Removes specified columns from the table for tender-specific views
     */
    static setColumnsForTender(table: Table, columnsToRemove: string[]) {
        columnsToRemove.forEach(columnId => {
            const column = table.columns.find(column => columnId === column.headingCell.id);
            if (column) {
                table.removeColumn(column);
            }
        });
    }

    /**
     * Sets the enabled state for the enable/disable buttons
     */
    static setEnabledButtonEnabledProperties(buttonDisableAll: Button, buttonEnableAll: Button, isEnabled: boolean) {
        buttonDisableAll.enabled = isEnabled;
        buttonEnableAll.enabled = isEnabled;
    }

    /**
     * Common logic for enabling/disabling records
     */
    static async setRecordsOnClick(
        table: Table, 
        mainDataSource: DataSource, 
        isEnabled: boolean, 
        confirmationMessage: string, 
        button: Button
    ) {
        button.busy = true;
        const selectedRows = this.returnFormattedRows(table);
        const yes = await CommonDialogs.showYesNo(confirmationMessage, "Confirm Action", { 
            titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } 
        });
        if (yes) {
            table.busy = true;
            await Api.post("lme/datafusion/enable-disable-partner-codes", { ids: selectedRows, is_enabled: isEnabled });
            await mainDataSource.search(mainDataSource.lastSearch);
            table.busy = false;
        }
        button.busy = false;
    }

    /**
     * Returns formatted rows for API calls
     */
    static returnFormattedRows(table: Table) {
        return table.data.map(row => ({ id: row.get("id") }));
    }

    /**
     * Common logic for setting up lookup model search filters
     */
    static setupLookupFilter(event: LookupModelSearchEvent, partnerId: string, version: string, isReply: boolean) {
        event.filter.partner_id_input = partnerId;
        event.filter.version_input = version;
        event.filter.reply = isReply;
    }

    /**
     * Common logic for setting up lookup model search filters
     */
    static setupLookupFilterDelay(event: LookupModelSearchEvent, partnerId: string, version: string, isDelay: boolean) {
        event.filter.partner_id_input = partnerId;
        event.filter.version_input = version;
        event.filter.delay_reason = isDelay;
    }

    /**
     * Common logic for handling lookup model selections
     */
    static handleLookupSelection(event: LookupModelSelectionEvent, partnerId: string, version: string) {
        if (event.hasSelection()) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            const modelRow = tableRow?.data as ModelRow;
            
            // Set common fields
            modelRow.set("element_id", event.selection.get("element_id"));
            modelRow.set("description", event.selection.get("description"));
            modelRow.set("partner_id", partnerId);
            modelRow.set("version", version);
            
            // Update UI components
            this.updateTextboxFromSelection(tableRow, "textboxDescription", event.selection.get("description"));
            this.updateTextboxFromSelection(tableRow, "textboxElementId", event.selection.get("element_id"));
        }
    }

    /**
     * Helper method to update textbox values
     */
    private static updateTextboxFromSelection(tableRow: TableRow, textboxId: string, value: any) {
        const textbox: Textbox = tableRow?.findComponentById(textboxId) as Textbox;
        if (textbox) {
            textbox.text = value;
        }
    }
}
